import {
  forwardRef,
  ReactNode,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { ChevronRight } from '@/components/atoms/assets/ChevronRight'
import { Dollar } from '@/components/atoms/assets/Dollar'
import { Logo } from '@/components/atoms/assets/Logo'
import { Modal, ModalRef } from '@/components/molecules/Modal'

import { useWhiteList } from '@/lib/hooks/useWhiteList'
import { Routes } from '@/router/routes'

interface DepositModalProps {
  onBuyCurrency: () => void
}

export interface DepositModalRef {
  closeModal: () => void
  openModal: () => void
}

export const DepositModal = forwardRef(
  ({ onBuyCurrency }: DepositModalProps, ref) => {
    const { t } = useTranslation()
    const history = useHistory()
    const modalRef = useRef<ModalRef>()
    const { isWhitelistedUser } = useWhiteList()

    const receiveRPK = useCallback(() => {
      modalRef?.current?.closeModal()
      history.push(Routes.DEPOSIT)
    }, [history])

    const buyRPK = useCallback(() => {
      modalRef?.current?.closeModal()
      onBuyCurrency()
    }, [onBuyCurrency])

    useImperativeHandle(ref, () => ({
      closeModal: () => modalRef.current.closeModal(),
      openModal: () => modalRef.current.openModal(),
    }))

    return (
      <Modal ref={modalRef}>
        <div className="w-full">
          <p className="font-bold text-lg mt-2">
            {t('depositBottomSheet.receive')}
          </p>
          <div className="my-12">
            <ReceiveModalButton
              title={t('depositBottomSheet.buyRPK')}
              description={t('depositBottomSheet.currencyViaBanxa')}
              icon={<Dollar className="fill-white w-6 h-6" />}
              onClick={buyRPK}
              disabled={!isWhitelistedUser}
            />
            <ReceiveModalButton
              title={t('depositBottomSheet.receiveRPK')}
              description={t('depositBottomSheet.fromHotWalletOrExchange')}
              icon={<Logo className="fill-white w-6 h-6" />}
              onClick={receiveRPK}
            />
          </div>
        </div>
      </Modal>
    )
  }
)

DepositModal.displayName = 'DepositModal'

interface ReceiveModalButtonProps {
  title: string
  description: string
  icon?: ReactNode
  onClick: () => void
  disabled?: boolean
}

function ReceiveModalButton({
  title,
  description,
  icon,
  onClick,
  disabled,
}: ReceiveModalButtonProps) {
  return (
    <div
      className={`w-full text-left flex-row flex justify-between items-center my-10 ${
        disabled && 'opacity-60 cursor-not-allowed'
      } ${!disabled && 'hover:opacity-60 cursor-pointer'}`}
      onClick={disabled ? undefined : onClick}
    >
      <div className="flex flex-row w-full">
        {icon && (
          <div>
            <div className="mr-6 border rounded-full p-2 border-2">{icon}</div>
          </div>
        )}

        <div className="flex flex-col grow">
          <div className="flex flex-row justify-between items-center">
            <p className="text-primary font-bold text-3xl">{title}</p>
            <ChevronRight className="w-6 h-6" />
          </div>

          <p className="text-sm text-cool-gray-200">{description}</p>
        </div>
      </div>
    </div>
  )
}
